import { signalStore, withState, withMethods, withComputed, patchState } from '@ngrx/signals';
import { computed, inject } from '@angular/core';
import { SearchService } from 'src/app/services/search.service';
import { UserService } from 'src/app/services/user.service';
import { FavoritesService } from 'src/app/services/favorites.service';
import { ToastrService } from 'ngx-toastr';
import { TranslateService } from '@ngx-translate/core';
import { Asset } from 'src/app/components/models/asset.model';
import { EMPTY, firstValueFrom } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { Router } from '@angular/router';
import { AuthStore } from './auth.store';

export interface DomainInfo {
  description: string;
  name: string;
  isValid: boolean;
}

export interface PublicUser {
  id?: string;
  shortId?: string;
  email?: string;
  name?: string;
  publicName?: string;
  websiteUrl?: string;
  bio?: string;
  country?: string;
  hasProfilePicture?: boolean;
  domains?: DomainInfo[];
}

export interface PublicProfileState {
  profileInfo: PublicUser;
  userAcceptedAssets: Asset[];
  alreadyFavorite: boolean;
  loading: boolean;
  profileNotFound: boolean;
  resumedProfile: boolean;
}

const initialState: PublicProfileState = {
  profileInfo: {},
  userAcceptedAssets: [],
  alreadyFavorite: false,
  loading: false,
  profileNotFound: false,
  resumedProfile: false,
};

export const PublicProfileStore = signalStore(
  { providedIn: 'root' },
  withState(initialState),

  withComputed(({ profileInfo, loading, userAcceptedAssets, alreadyFavorite, resumedProfile }) => {
    const authStore = inject(AuthStore);

    return {
      targetDomains: computed(() => profileInfo()?.domains),
      isLoading: computed(() => loading()),
      avatarUrl: computed(() =>
        profileInfo() ? `${environment.api}User/get-public-profile-picture?userId=${profileInfo().id}` : '',
      ),
      displayName: computed(() => {
        const profile = profileInfo();
        return profile?.publicName || profile?.name || '';
      }),
      hasBio: computed(() => {
        return !!profileInfo()?.bio;
      }),
      canMakePayment: computed(() => {
        return !!profileInfo()?.shortId && userAcceptedAssets().length > 0;
      }),
      hasAcceptedAssets: computed(() => {
        return userAcceptedAssets().length > 0;
      }),
      isMyProfile: computed(() => {
        const profile = profileInfo();
        const authProfile = authStore.profile();
        return profile?.id === authProfile?.id;
      }),
      showConnectButton: computed(() => {
        return authStore.authenticated();
      }),
      connectButtonIcon: computed(() => {
        return alreadyFavorite() ? 'ph ph-check-circle text-green-500' : 'ph ph-plus';
      }),
      hasValidProfileData: computed(() => {
        const profile = profileInfo();
        return !!(profile?.id || profile?.shortId || profile?.email);
      }),
      resumedProfile: computed(() => {
        return resumedProfile();
      }),
    };
  }),

  withMethods((store) => {
    const searchService = inject(SearchService);
    const userService = inject(UserService);
    const favoritesService = inject(FavoritesService);
    const toastr = inject(ToastrService);
    const translate = inject(TranslateService);
    const router = inject(Router);

    const loadProfile = (value: string, type: 'shortId' | 'email' | 'domain') => {
      patchState(store, { loading: true });

      const searchMethod = {
        shortId: () => searchService.getPublicProfileByShortId(value),
        email: () => searchService.getPublicProfileByEmail(value),
        domain: () => searchService.getPublicProfileByEmail(value),
      }[type];

      searchMethod()
        .pipe(
          tap(async (response: any) => {
            if (!response) {
              patchState(store, { profileNotFound: true, loading: false });
              router.navigate(['/error']);
              return;
            }

            const profile = {
              ...response,
              avatar: `${environment.api}User/get-public-profile-picture?userId=${response.id}`,
            };

            setProfileInfo(response);

            await Promise.all([loadAcceptedAssets(profile.id), checkFavorite(profile.email)]);

            patchState(store, { loading: false });
          }),
          catchError((err) => {
            console.error('Erro ao buscar perfil público', err);
            toastr.error(translate.instant('Failed to load profile'));
            patchState(store, { loading: false });
            router.navigate(['/error']);
            return EMPTY;
          }),
        )
        .subscribe();
    };

    const loadFullProfileInfo = async (profile: PublicUser) => {
      return await Promise.all([loadAcceptedAssets(profile.id || ''), checkFavorite(profile.email || '')]);
    };

    const setProfileInfo = async (profileInfo: PublicUser, resumedProfile = false) => {
      patchState(store, { profileInfo, resumedProfile });
      if (resumedProfile) {
        return;
      }
      loadFullProfileInfo(profileInfo);
    };

    const loadAcceptedAssets = async (userId: string) => {
      const assets = await firstValueFrom(userService.getReceiverConnections(userId));
      const uniqueAssets = Array.from(new Map(assets.map((asset) => [asset.assetTicker, asset])).values());
      patchState(store, {
        userAcceptedAssets: uniqueAssets.sort((a, b) => a.assetTicker.localeCompare(b.assetTicker)),
      });
    };

    const checkFavorite = (email: string) => {
      return favoritesService
        .searchFavorites(email, 1, 0)
        .pipe(
          tap((response: any) => {
            patchState(store, { alreadyFavorite: !!response?.data?.[0] });
          }),
          catchError(() => {
            patchState(store, { alreadyFavorite: false });
            return EMPTY;
          }),
        )
        .subscribe();
    };

    const addFavorite = (email: string) => {
      favoritesService
        .addFavorite(email)
        .pipe(
          tap(() => {
            toastr.success(translate.instant('userAddedToYourFavoritesSuccessfully'), '', { timeOut: 3000 });
            patchState(store, { alreadyFavorite: true });
          }),
          catchError((error) => {
            console.error(error);
            toastr.error(translate.instant('errorAddingFavorite'));
            return EMPTY;
          }),
        )
        .subscribe();
    };

    const removeFavorite = (email: string) => {
      favoritesService
        .deleteFavorite(email)
        .pipe(
          tap(() => {
            toastr.success(translate.instant('theNickHasBeenSuccessfullyRemoved'), '', { timeOut: 3000 });
            patchState(store, { alreadyFavorite: false });
          }),
          catchError((error) => {
            console.error(error);
            toastr.error(translate.instant('errorRemovingFavorite'));
            return EMPTY;
          }),
        )
        .subscribe();
    };

    return {
      loadProfile,
      setProfileInfo,
      addFavorite,
      removeFavorite,
      checkFavorite,
    };
  }),
);
